import { useEffect, useState } from 'react';
import {
  Button,
  ComposedModal,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@carbon/react';
import {
  nextWorkspaceName,
  validateWorkspaceName,
  type WorkspaceNameProblem,
} from './workspace-naming';
import style from './create-workspace-modal.module.scss';

/** What the field says about a name it can't accept. */
const NAME_ERRORS: Record<Exclude<WorkspaceNameProblem, null>, string> = {
  required: 'Workspace name is required.',
  duplicate: 'This area already has a workspace with this name.',
};

/** The one workspace type an area can carry, shown as read-only context. */
const WORKSPACE_TYPE = 'Area Workspace';

export interface CreateWorkspaceModalProps {
  open: boolean;
  /** The area the workspace is opened on, as the context row shows it. */
  areaName: string;
  // Names already on the area. Drives both the suggested name and the duplicate check,
  // so pass every workspace the area carries, promoted or not.
  existingNames: string[];
  /** True while the create request is in flight. */
  pending: boolean;
  /** Why the last attempt was refused, or null. */
  error: string | null;
  onClose: () => void;
  onCreate: (name: string) => void;
}

// Names a new workspace before opening it. Type and area are fixed by the launch point,
// so they are shown as context rather than offered as choices.
export const CreateWorkspaceModal = ({
  open,
  areaName,
  existingNames,
  pending,
  error,
  onClose,
  onCreate,
}: CreateWorkspaceModalProps): JSX.Element => {
  const [name, setName] = useState('');

  // Re-suggest on each open: a workspace created a moment ago has taken the name
  // this modal last proposed, and reopening should not offer it again.
  useEffect(() => {
    if (open) {
      setName(nextWorkspaceName(existingNames));
    }
    // `existingNames` is out of the deps: a fresh array each render would re-suggest
    // mid-typing and overwrite the field.

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const problem = validateWorkspaceName(name, existingNames);

  return (
    // ComposedModal, not `Modal`, as the mock and both sibling create modals use:
    // `Modal` drops `size` and gives no handle on its generated footer's Cancel.
    <ComposedModal
      open={open}
      size="sm"
      // No way out mid-write: the workspace is either created or it isn't, and
      // closing early would leave the page unsure which.
      onClose={pending ? () => false : onClose}
      preventCloseOnClickOutside={pending}
      className={style.modal}
    >
      <ModalHeader label={WORKSPACE_TYPE} title="Create Workspace" />
      <ModalBody aria-label="Create area workspace">
        {error && (
          <InlineNotification
            kind="error"
            title="Could not create the workspace"
            subtitle={error}
            lowContrast
            hideCloseButton
            className={style.notification}
          />
        )}

        <p className={style.intro}>
          Inside the workspace you can review and adjust this area&apos;s
          geometry and details before promoting it to Insights map layers. Past
          workspaces stay with the area as a record of the work that has been
          completed.
        </p>

        <div className={style.context}>
          <div className={style.field}>
            <span className={style.fieldLabel}>Workspace type</span>
            <span className={style.fieldValue}>{WORKSPACE_TYPE}</span>
          </div>
          <div className={style.field}>
            <span className={style.fieldLabel}>Area</span>
            <span className={style.fieldValue}>{areaName}</span>
          </div>
        </div>

        <TextInput
          id="area-workspace-name"
          labelText="Workspace name"
          helperText="Each workspace is kept with this area as a record of the work completed."
          value={name}
          invalid={problem !== null}
          invalidText={problem ? NAME_ERRORS[problem] : undefined}
          disabled={pending}
          onChange={(event) => setName(event.target.value)}
          // Carbon focuses whatever carries this when the modal opens, so the name
          // is ready to overtype rather than needing a click first.
          data-modal-primary-focus
        />
      </ModalBody>
      <ModalFooter>
        <Button
          kind="secondary"
          className={style.cancelButton}
          disabled={pending}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          disabled={pending || problem !== null}
          onClick={() => onCreate(name.trim())}
        >
          {pending ? 'Creating…' : 'Create Workspace'}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};
